import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { AsyncPipe } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    signal,
    ViewChild,
} from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AccessibilityService } from '@core/services/accessibility.service';
import { CalcCssVarsService } from '@core/services/calc-css-vars.service';
import { NetworkService } from '@core/services/network.service';
import { PlatformService } from '@core/services/platform.service';
import { ScreenOrientationService } from '@core/services/screen-orientation.service';
import { ToastService } from '@core/services/toast.service';
import { UpdateAppService } from '@core/services/update-app.service';
import { IonicModule, IonRouterOutlet, NavController, Platform } from '@ionic/angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DrugsStore } from '@store/drugs/drugs.store';
import { addIcons } from 'ionicons';
import {
    addOutline,
    alertCircleOutline,
    arrowBackCircleOutline,
    arrowBackOutline,
    arrowForwardCircleOutline,
    arrowForwardOutline,
    calendarOutline,
    caretDownOutline,
    caretUpOutline,
    checkmarkOutline,
    chevronBackOutline,
    chevronDownOutline,
    chevronForwardOutline,
    chevronUpOutline,
    closeCircleOutline,
    closeOutline,
    cloudOfflineOutline,
    contrastOutline,
    copyOutline,
    createOutline,
    ellipse,
    exitOutline,
    eyeOffOutline,
    eyeOutline,
    fingerPrintOutline,
    globeOutline,
    informationCircleOutline,
    lockClosedOutline,
    lockOpenOutline,
    notificationsOutline,
    playBackOutline,
    playForwardOutline,
    readerOutline,
    refreshOutline,
    removeOutline,
    repeatOutline,
    searchOutline,
    settingsOutline,
    square,
    timeOutline,
    todayOutline,
    trash,
    triangle,
    warning,
    warningOutline,
} from 'ionicons/icons';
import { BehaviorSubject, fromEvent, Subject, Subscription } from 'rxjs';
import { first, takeUntil, throttleTime } from 'rxjs/operators';
import { AccessibilityPanelComponent } from 'src/app/components/accessibility-panel/accessibility-panel.component';
import { FooterComponent } from 'src/app/components/footer/footer.component';
import { HeaderComponent } from 'src/app/components/header/header.component';

@Component({
    selector: 'ncodp-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonicModule, AsyncPipe, TranslatePipe, HeaderComponent, FooterComponent, AccessibilityPanelComponent, CdkScrollableModule],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    public isIosPlatform = this.platformService.isIosPlatform$;
    public isEmptyConnection$ = new BehaviorSubject<boolean>(true);
    public showGlobalScrollTop = signal(false);
    private readonly navCtrl = inject(NavController);
    private platform = inject(Platform);
    private calcCssVarsService = inject(CalcCssVarsService);
    private readonly elementRef = inject(ElementRef);
    private lastTimeBackButtonWasPressed = 0;
    private timePeriodToAction = 2000;
    private destroyedNg$ = new Subject<void>();
    private backButtonBlockSub: Subscription | null = null;
    private orientationChangeListener: PluginListenerHandle | null = null;
    private drugsStore = inject(DrugsStore);
    public isLoadingDrug = computed(() => this.drugsStore.isLoadingDrug());
    @ViewChild('routerOutlet') private routerOutlet!: IonRouterOutlet;

    constructor(
        private updateAppService: UpdateAppService,
        private platformService: PlatformService,
        private networkService: NetworkService,
        private cdr: ChangeDetectorRef,
        private toastService: ToastService,
        private translate: TranslateService,
        private screenOrientationService: ScreenOrientationService,
        private accessibilityService: AccessibilityService,
    ) {
        addIcons({
            refreshOutline,
            chevronForwardOutline,
            chevronUpOutline,
            lockOpenOutline,
            chevronBackOutline,
            cloudOfflineOutline,
            repeatOutline,
            settingsOutline,
            alertCircleOutline,
            warningOutline,
            checkmarkOutline,
            addOutline,
            arrowBackOutline,
            arrowBackCircleOutline,
            arrowForwardCircleOutline,
            calendarOutline,
            chevronDownOutline,
            copyOutline,
            ellipse,
            exitOutline,
            eyeOffOutline,
            eyeOutline,
            fingerPrintOutline,
            globeOutline,
            informationCircleOutline,
            lockClosedOutline,
            notificationsOutline,
            playBackOutline,
            playForwardOutline,
            readerOutline,
            square,
            timeOutline,
            todayOutline,
            trash,
            triangle,
            createOutline,
            arrowForwardOutline,
            searchOutline,
            closeCircleOutline,
            warning,
            caretDownOutline,
            caretUpOutline,
            removeOutline,
            closeOutline,
            contrastOutline,
        });

        if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
            this.screenOrientationService.initListener();
            this.platformService.init();
            this.networkService.init();
            this.updateAppService.checkUpdate().pipe(first(), takeUntil(this.destroyedNg$)).subscribe();
        }
        this.calcCssVarsService.initialize();
    }

    public async ngOnInit() {
        this.backButtonBlockSub = this.platform.backButton.subscribeWithPriority(1, () => {
            console.log('backButtonBlockSub app.component');
            this.navCtrl.back();
        });

        this.networkService.networkStatusChange$.pipe(takeUntil(this.destroyedNg$)).subscribe((connected) => {
            this.isEmptyConnection$.next(!connected);
            this.cdr.detectChanges();
        });
        this.platformService.backButton().subscribeWithPriority(1, () => {
            this.withDoublePress(this.translate.instant('common.exit-message'), () => {
                App.minimizeApp().then();
            }).then();
        });

        if (Capacitor.getPlatform() !== 'web') {
            await StatusBar.setOverlaysWebView({ overlay: true });
            await StatusBar.setBackgroundColor({ color: '#00000000' });
            await StatusBar.setStyle({ style: Style.Light });
            console.log('StatusBar set');
        }
    }

    public ngAfterViewInit() {
        if (this.calcCssVarsService.isNotDesktopView$.value) {
            const el = this.routerOutlet.nativeEl;
            el.style.overflowY = 'auto';

            fromEvent(el, 'scroll', { passive: true })
                .pipe(throttleTime(80, undefined, { trailing: true }), takeUntil(this.destroyedNg$))
                .subscribe(() => {
                    const yOffset = el.scrollTop || el.scrollTop || 0;
                    this.showGlobalScrollTop.set(yOffset > 100);
                });
        }
    }

    public ngOnDestroy() {
        this.networkService.removeAllListeners();
        this.destroyedNg$.next();
        this.destroyedNg$.complete();
        this.orientationChangeListener?.remove();
        this.backButtonBlockSub?.unsubscribe();
    }

    public scrollToTop(): void {
        this.routerOutlet.nativeEl.scrollTo({ top: 0, behavior: 'smooth' });
    }

    private async withDoublePress(message: string, action: () => void) {
        const currentTime = new Date().getTime();
        if (
            currentTime - this.lastTimeBackButtonWasPressed < this.timePeriodToAction &&
            currentTime - this.lastTimeBackButtonWasPressed > 200
        ) {
            action();
            this.lastTimeBackButtonWasPressed = currentTime;
            this.toastService
                .openToast(message, 'dark', 'center bottom default', 2000)
                .pipe(first(), takeUntil(this.destroyedNg$))
                .subscribe();
        }
    }
}
